/** Lobby: share the room, add bots, set the rules, deal. scope.md §8.1. */

import { useState } from 'react';
import type { BotTier, RoomRules, Translate } from '@garbage/shared';
import { ALLOWED_CLAIM_WINDOWS_MS, BUILT_IN_RULE_SETS } from '@garbage/shared';
import type { LobbyPlayer } from '../protocol.js';
import { InviteSheet } from './InviteSheet.js';
import { useT } from '../i18n.js';
import { SettingsButton } from './SettingsSheet.js';

/** The rule sets that ship with the game, whose names are translated. */
type BuiltInId = 'house' | 'traditional' | 'gentle';

/** Bots keep their names in every language; what they are like is translated. */
const TIERS: { id: BotTier; label: string }[] = [
  { id: 'bumble', label: 'Bumble' },
  { id: 'scout', label: 'Scout' },
  { id: 'ace', label: 'Ace' },
  { id: 'robo', label: 'Robo' },
];

export function Lobby({
  code, players, rules, isHost, onAddBot, onRemove, onDeal, onRules, onHelp, origin, onSettings,
}: {
  code: string;
  players: LobbyPlayer[];
  rules: RoomRules | null;
  isHost: boolean;
  onAddBot: (tier: BotTier) => void;
  onRemove: (id: string) => void;
  onDeal: () => void;
  onRules: (rules: Partial<RoomRules>) => void;
  onHelp: () => void;
  origin: string;
  onSettings?: () => void;
}) {
  const t = useT();
  const [inviting, setInviting] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);

  return (
    <main className="flex h-full flex-col gap-4 overflow-y-auto px-5 pt-[calc(1.5rem+var(--pad-top))] pb-[calc(1.5rem+var(--pad-bottom))]">
      <div className="relative text-center">
        {onSettings && (
          <div className="absolute right-0 top-0">
            <SettingsButton onClick={onSettings} />
          </div>
        )}
        <p className="text-xs font-bold uppercase tracking-wide text-bone/50">{t('lobby.roomCode')}</p>
        <p data-testid="room-code" className="font-mono text-6xl font-black tracking-[0.2em] text-sun">
          {code}
        </p>
      </div>

      <button
        type="button"
        data-testid="share-button"
        onClick={() => setInviting(true)}
        className="rounded-2xl bg-sun py-4 text-lg font-black text-ink active:scale-95"
      >
        {t('lobby.invite')}
      </button>

      {inviting && (
        <InviteSheet code={code} origin={origin} onClose={() => setInviting(false)} />
      )}

      <section>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-bone/50">
          {t('lobby.players', { count: players.length })}
        </h2>
        <ul className="flex flex-col gap-1.5" data-testid="player-list">
          {players.map((player) => (
            <li
              key={player.id}
              data-testid={`player-${player.id}`}
              className="flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2"
            >
              <span className="flex-1 truncate font-bold">{player.name}</span>
              {player.bot && <span className="text-xs text-bone/50">{player.bot}</span>}
              {!player.connected && <span className="text-xs text-coral">{t('lobby.offline')}</span>}
              {isHost && player.bot && (
                <button
                  type="button"
                  aria-label={t('lobby.removePlayer', { name: player.name })}
                  onClick={() => onRemove(player.id)}
                  className="rounded-lg bg-white/10 px-2 text-sm"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      {isHost && (
        <>
          <section>
            <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-bone/50">{t('lobby.addBot')}</h2>
            <div className="grid grid-cols-2 gap-2">
              {TIERS.map((tier) => (
                <button
                  key={tier.id}
                  type="button"
                  data-testid={`add-bot-${tier.id}`}
                  onClick={() => onAddBot(tier.id)}
                  className="rounded-xl bg-white/10 px-3 py-2 text-left active:bg-white/20"
                >
                  <span className="block font-bold">{tier.label}</span>
                  <span className="block text-[11px] text-bone/50">{t(`bot.${tier.id}.blurb`)}</span>
                </button>
              ))}
            </div>
          </section>

          <section>
            <div className="mb-2 flex items-baseline gap-2">
              <h2 className="text-xs font-bold uppercase tracking-wide text-bone/50">{t('lobby.rules')}</h2>
              <span className="text-xs font-bold text-bone" data-testid="ruleset-name">
                {namedRuleSet(rules, t)}
              </span>
              <button
                type="button"
                data-testid="toggle-options"
                aria-expanded={optionsOpen}
                onClick={() => setOptionsOpen((open) => !open)}
                className="ml-auto rounded-lg bg-white/10 px-3 text-xs font-bold"
              >
                {optionsOpen ? t('common.done') : t('lobby.change')}
              </button>
            </div>

            {optionsOpen && (
              <div data-testid="options">
                <div className="mb-3 grid grid-cols-3 gap-2">
                  {BUILT_IN_RULE_SETS.map((set) => ({ ...set, id: set.id as BuiltInId })).map((set) => (
                    <button
                      key={set.id}
                      type="button"
                      data-testid={`ruleset-${set.id}`}
                      onClick={() => onRules(set.rules)}
                      className="rounded-xl bg-white/10 px-2 py-2 text-left active:bg-white/20"
                    >
                      <span className="block text-sm font-bold">{t(`ruleset.${set.id}.name`)}</span>
                      <span className="block text-[10px] leading-tight text-bone/50">
                        {t(`ruleset.${set.id}.description`)}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="flex flex-col gap-2 text-sm">
                  <Choice
                    label={t('rules.ace')}
                    value={rules?.ace ?? 'both'}
                    options={[['both', t('rules.ace.both')], ['low', t('rules.ace.low')], ['high', t('rules.ace.high')]]}
                    onChange={(value) => onRules({ ace: value as 'both' })}
                  />
                  <Choice
                    label={t('rules.timeToAsk')}
                    value={rules?.claimWindowMs ?? 5000}
                    options={ALLOWED_CLAIM_WINDOWS_MS.map((ms): [number, string] => [ms, t('seconds.short', { count: ms / 1000 })])}
                    onChange={(value) => onRules({ claimWindowMs: value as 5000 })}
                  />
                  <Choice
                    label={t('rules.claimPenalty')}
                    value={rules?.claimPenalty ?? 'oneCard'}
                    options={[['oneCard', t('common.yes')], ['none', t('common.no')]]}
                    onChange={(value) => onRules({ claimPenalty: value as 'oneCard' })}
                  />
                  <Choice
                    label={t('rules.lastRound')}
                    value={rules?.finalContract ?? 'threeRunsAllCards'}
                    options={[['threeRunsAllCards', t('rules.lastRound.allCards')], ['threeRunsOf4', t('rules.lastRound.threeRunsOf4')]]}
                    onChange={(value) => onRules({ finalContract: value as 'threeRunsAllCards' })}
                  />
                </div>

              </div>
            )}

            <button
              type="button"
              data-testid="how-to-play"
              onClick={onHelp}
              className="mt-3 w-full rounded-xl bg-white/10 py-2 text-sm font-bold"
            >
              {t('lobby.howToPlayRules')}
            </button>
          </section>

          <button
            type="button"
            data-testid="deal-button"
            disabled={players.length < 2}
            onClick={onDeal}
            className="mt-auto rounded-2xl bg-sun py-5 text-2xl font-black text-ink disabled:opacity-30"
          >
            {t('lobby.deal')}
          </button>
        </>
      )}

      {!isHost && (
        <p className="mt-auto text-center text-sm text-bone/60">
          {t('lobby.waitingForHost')}
        </p>
      )}
    </main>
  );
}

function Choice<T extends string | number>({
  label, value, options, onChange,
}: {
  label: string; value: T; options: [T, string][]; onChange: (value: T) => void;
}) {
  return (
    // When the choices will not fit beside the label they drop underneath it together,
    // rather than overflowing or splitting across two lines.
    <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
      <span className="flex-1 whitespace-nowrap text-bone/70">{label}</span>
      <div className="flex gap-2">
        {options.map(([option, text]) => (
          <button
            key={String(option)}
            type="button"
            aria-pressed={value === option}
            onClick={() => onChange(option)}
            className={[
              'rounded-lg px-2.5 text-xs font-bold',
              value === option ? 'bg-sun text-ink' : 'bg-white/10',
            ].join(' ')}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Which of the built-in sets these rules match, if any. */
function namedRuleSet(rules: RoomRules | null, t: Translate): string {
  if (!rules) return t('ruleset.house.name');
  const match = BUILT_IN_RULE_SETS.find(
    (set) => JSON.stringify(set.rules) === JSON.stringify(rules),
  );
  return match ? t(`ruleset.${match.id as BuiltInId}.name`) : t('ruleset.custom');
}
